import { useState, useRef } from 'react';
import { Mic, Square, Play, Loader2, Trash2, Save } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getCompanyId } from '../utils/tenantContext';
import { useAuth } from '../hooks/useAuth';

interface EscalationVoiceRecorderProps {
  onRecordingComplete: (url: string) => void;
  existingUrl?: string;
}

export default function EscalationVoiceRecorder({ onRecordingComplete, existingUrl }: EscalationVoiceRecorderProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(existingUrl || null);
  const [isUploading, setIsUploading] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const { user } = useAuth();

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      // Stop all tracks to release microphone
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
  };

  const playRecording = () => {
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.play();
    }
  };

  const deleteRecording = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    if (existingUrl) {
      onRecordingComplete(''); // Clear the URL in parent
    }
  };

  const saveRecording = async () => {
    if (!audioBlob) return;
    if (!user) {
      toast.error('You must be logged in to save recordings');
      return;
    }

    setIsUploading(true);
    try {
      const storage = getStorage();
      const companyId = getCompanyId();
      // Path: companies/{companyId}/users/{userId}/voice_recording_{timestamp}.webm
      // This matches the user's request for "companies companyid users userid voice recorder"
      // and complies with storage rules that allow user-specific writes
      const filename = `voice_recording_${Date.now()}.webm`;
      const storageRef = ref(storage, `companies/${companyId}/users/${user.id}/voice_recordings/${filename}`);

      await uploadBytes(storageRef, audioBlob, {
        customMetadata: {
          companyId,
          uploadedBy: user.id
        }
      });
      const downloadUrl = await getDownloadURL(storageRef);

      onRecordingComplete(downloadUrl);
      toast.success('Voice recording saved successfully');
    } catch (error) {
      console.error('Error uploading recording:', error);
      toast.error('Failed to save recording');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="bg-slate-50/50 dark:bg-zinc-800/50 backdrop-blur-sm p-3 rounded-xl border border-slate-200 dark:border-zinc-700 mt-2 transition-colors">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Voice Message</span>
        {audioUrl && (
          <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1">
            {existingUrl === audioUrl ? 'Saved' : 'Recorded (Unsaved)'}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {!isRecording && !audioUrl && (
          <Button
            size="sm"
            variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-900/30 dark:hover:bg-red-900/20 bg-white dark:bg-zinc-900"
            onClick={startRecording}
          >
            <Mic className="w-4 h-4 mr-2" />
            Record Message
          </Button>
        )}

        {isRecording && (
          <Button
            size="sm"
            variant="destructive"
            className="animate-pulse bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 text-white"
            onClick={stopRecording}
          >
            <Square className="w-4 h-4 mr-2" />
            Stop Recording
          </Button>
        )}

        {audioUrl && !isRecording && (
          <>
            <Button size="sm" variant="outline" onClick={playRecording} className="bg-white dark:bg-zinc-900 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800">
              <Play className="w-4 h-4 mr-2" />
              Play
            </Button>
            
            {!existingUrl && (
               <Button 
                size="sm" 
                className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/20"
                onClick={saveRecording}
                disabled={isUploading}
              >
                {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save
              </Button>
            )}

            <Button
              size="sm"
              variant="ghost"
              className="text-slate-400 hover:text-red-500 dark:text-slate-500 dark:hover:text-red-400"
              onClick={deleteRecording}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>
      <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-2">
        This message will be played when the automated call is answered.
      </p>
    </div>
  );
}
